import React, { useState, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

// Set up worker for PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

function PDFViewer({ pdfUrl, forceReloadKey = 0 }) {
  const [numPages, setNumPages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const [lastRetryTime, setLastRetryTime] = useState(0); // Track when the last retry was attempted
  const [scale, setScale] = useState(1.7);
  const [visiblePages, setVisiblePages] = useState({});
  const containerRef = useRef(null);
  const timerRef = useRef(null);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setLoading(false);
    setRetrying(false);
    setRetryCount(0); // Reset retry count on success
    console.log(`PDF loaded successfully with ${numPages} pages`);
    
    // Initialize with first few pages
    const initialVisiblePages = {};
    for (let i = 1; i <= Math.min(3, numPages); i++) {
      initialVisiblePages[i] = true;
    }
    setVisiblePages(initialVisiblePages);
  }

  function onDocumentLoadError(error) {
    const currentTime = new Date();
    const timeSinceLastRetry = currentTime.getTime() - lastRetryTime;
    const timeDisplay = currentTime.toLocaleTimeString();
    
    console.warn(`[${timeDisplay}] Error loading PDF`, error);
    
    // If we've attempted a retry too recently, don't immediately try again
    if (timeSinceLastRetry < 5000 && lastRetryTime !== 0) {
      console.log(`[${timeDisplay}] Last retry was only ${timeSinceLastRetry}ms ago, enforcing 5-second minimum interval`);
      
      // Clear any existing timer to avoid multiple timers
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      
      // Setup a new retry after the remaining time to reach 5 seconds
      const remainingTime = Math.max(5000 - timeSinceLastRetry, 100); // at least 100ms
      console.log(`[${timeDisplay}] Scheduling next retry in ${remainingTime}ms`);
      
      setRetrying(true);
      
      timerRef.current = setTimeout(() => {
        const executeTime = new Date().toLocaleTimeString();
        console.log(`[${executeTime}] Executing scheduled retry #${retryCount + 1}`);
        setLastRetryTime(new Date().getTime());
        setRetryCount(prevCount => prevCount + 1);
      }, remainingTime);
      
      return;
    }
    
    // If we're here, either this is the first retry or it's been at least 5 seconds
    console.log(`[${timeDisplay}] Scheduling retry #${retryCount + 1} in 5 seconds`);
    
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    // Set retrying state to true so we show the loading indicator
    setRetrying(true);
    
    // Schedule the retry in 5 seconds
    timerRef.current = setTimeout(() => {
      const executeTime = new Date().toLocaleTimeString();
      console.log(`[${executeTime}] Executing scheduled retry #${retryCount + 1}`);
      setLastRetryTime(new Date().getTime());
      setRetryCount(prevCount => prevCount + 1);
    }, 5000);
  }
  
  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !numPages) return;

    const handleScroll = () => {
      // Get references to all page containers
      const pageElements = container.querySelectorAll('.PDFPageContainer');
      if (pageElements.length === 0) {
        setTimeout(handleScroll, 200);
        return;
      }
      
      const containerRect = container.getBoundingClientRect();
      const pagesInView = {};
      
      // Check which pages are visible
      pageElements.forEach((pageEl, index) => {
        const pageNumber = index + 1;
        const rect = pageEl.getBoundingClientRect();
        
        // Check if page is visible in the viewport with some buffer
        const isVisible = 
          (rect.top < containerRect.bottom + 700) && 
          (rect.bottom > containerRect.top - 700);
        
        pagesInView[pageNumber] = isVisible;
      });
      
      setVisiblePages(prevPages => {
        // Only update if there's a change
        const newPages = {...prevPages, ...pagesInView};
        if (JSON.stringify(prevPages) !== JSON.stringify(newPages)) {
          return newPages;
        }
        return prevPages;
      });
    };

    container.addEventListener('scroll', handleScroll);
    
    // Initial check after a short delay to allow render
    setTimeout(handleScroll, 100);
    
    return () => container.removeEventListener('scroll', handleScroll);
  }, [numPages]);

  // Reset loading state and force a reload when forceReloadKey changes
  useEffect(() => {
    console.log(`[${new Date().toLocaleTimeString()}] PDF reload triggered with key: ${forceReloadKey}`);
    setLoading(true);
    setRetrying(true);
    setRetryCount(0);
    setLastRetryTime(new Date().getTime());
  }, [forceReloadKey]);

  const renderPages = () => {
    if (!numPages) return null;
    
    const pages = [];
    for (let i = 1; i <= numPages; i++) {

      pages.push(
        <div key={`page_container_${i}`} className="PDFPageContainer">
          {visiblePages[i] ? (
            <Page 
              key={`page_${i}`}
              pageNumber={i} 
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className="PDFPage"
              scale={scale}
              loading={
                <div className="PDFPageLoading">
                  <FontAwesomeIcon icon={faSpinner} spin />
                </div>
              }
            />
          ) : (
            <div 
              className="PDFPagePlaceholder" 
  
            >
              <span>Page {i}</span>
            </div>
          )}
        </div>
      );
    }
    return pages;
  };

  return (
    <div className="PDFViewer">
      {(loading || retrying) && (
        <div className="PDFLoading">
          <FontAwesomeIcon icon={faSpinner} spin />
          <span>Loading document...</span>
        </div>
      )}
      
      <div 
        className="PDFScrollContainer" 
        ref={containerRef}
      >
        <Document
          key={`pdf-document-${retryCount}-${forceReloadKey}`} // Force re-render on retry or external reload request
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={null}
          error={null} // Hide the default error message
          className="PDFDocument"
        >
          {renderPages()}
        </Document>
      </div>
    </div>
  );
}

export default PDFViewer;
